import { Window } from "./Window";
import { Toolbar } from "@dhx/ts-toolbar";
import { ProLayout } from "@dhx/ts-layout";
import { NavigationBarEvents } from "@dhx/ts-navbar";
import { IWindowConfig, IDirectionConfig, WindowEvents } from "./types";
import { ScrollView } from "@dhx/ts-common/ScrollView";

export class ProWindow extends Window {
	protected _layout: ProLayout;
	public scrollView: ScrollView;

	constructor(config: IWindowConfig) {
		super(config);
	}

	protected _startResize(resizeConfig: IDirectionConfig) {
		const minWidth = this.config.minWidth | 100;
		const minHeight = this.config.minHeight | 100;
		const left = this._popup.offsetLeft;
		const top = this._popup.offsetTop;
		const width = this._popup.offsetWidth;
		const height = this._popup.offsetHeight;
		const root = this.getRootView().refs;

		let resizer;
		let resizeClassName;
		switch (true) {
			case resizeConfig.bottom && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--bottom_left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.bottom && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--bottom_right";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.top && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--top_left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.top && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--top-right";
				resizer = root.right;
				break;
			case resizeConfig.top:
				resizeClassName = "dhx_window-body-pointer--top";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.bottom:
				resizeClassName = "dhx_window-body-pointer--bottom";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--right";
				resizer = root.right;
				break;
		}
		resizer.el.classList.add("dhx_window-resizer--active");
		this.config.node.classList.add("dhx_window--stop_selection");
		this.config.node.classList.add(resizeClassName);
		const mousemove = (e: MouseEvent) => {
			const {
				containerInnerWidth,
				containerInnerHeight,
				containerXOffset,
				containerYOffset,
			} = this._getContainerParams();
			const size = {
				width: this._notInNode() ? e.pageX - left : e.pageX - this.config.node.offsetLeft - left,
				height: this._notInNode() ? e.pageY - top : e.pageY - this.config.node.offsetTop - top,
				left: this._notInNode() ? e.pageX : e.pageX - this.config.node.offsetLeft,
				top: this._notInNode() ? e.pageY : e.pageY - this.config.node.offsetTop,
			};
			if (resizeConfig.right) {
				if (size.width < minWidth) {
					size.width = minWidth;
				} else if (size.width > containerXOffset + containerInnerWidth - left) {
					size.width = containerXOffset + containerInnerWidth - left;
				}

				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.bottom) {
				if (size.height < minHeight) {
					size.height = minHeight;
				} else if (size.height > containerYOffset + containerInnerHeight - top) {
					size.height = containerYOffset + containerInnerHeight - top;
				}

				this._popup.style.height = size.height + "px";
			}
			if (resizeConfig.left) {
				if (left + width - size.left < minWidth) {
					size.left = left + width - minWidth;
				}
				size.width = left + width - size.left;

				this.config.left = size.left;
				this._popup.style.left = size.left + "px";
				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.top) {
				if (size.top < containerYOffset) {
					size.top = containerYOffset;
				} else if (top + height - size.top < minHeight) {
					size.top = top + height - minHeight;
				}
				size.height = top + height - size.top;

				this.config.top = size.top;
				this._popup.style.top = size.top + "px";
				this._popup.style.height = size.height + "px";
			}
			this.config.width = this._popup.offsetWidth;
			this.config.height = this._popup.offsetHeight;
			this.events.fire(WindowEvents.resize, [size, { left, top, height, width }, resizeConfig]);
			this.scrollView && this.scrollView.update();
		};
		const mouseup = () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
			this.config.node.classList.remove("dhx_window--stop_selection");
			this.config.node.classList.remove(resizeClassName);
			resizer.el.classList.remove("dhx_window-resizer--active");
		};
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousemove", mousemove);
	}

	protected _initUI() {
		const rows: any[] = [];
		const isHeader =
			(this.config.header || this.config.title || this.config.closable || this.config.movable) &&
			this.config.header !== false;

		if (isHeader) {
			rows.push({
				id: "header",
				height: "content",
				css: "dhx_window-header " + (this.config.movable ? "dhx_window-header--movable" : ""),
				on: {
					mousedown: this.config.movable && this._handlers.move,
					dblclick: this._handlers.headerDblClick,
				},
			});
		}
		rows.push({
			id: "content",
			css: isHeader ? "dhx_window-content" : "dhx_window-content-without-header",
		});
		if (this.config.footer) {
			rows.push({ id: "footer", height: "content", css: "dhx_window-footer" });
		}
		if (this.config.resizable) {
			rows.push({ id: "resizers", height: "content", css: "resizers" });
		}
		const layout = (this._layout = new ProLayout(this._popup, {
			css: "dhx_window" + (this.config.modal ? " dhx_window--modal" : ""),
			rows,
			on: {
				mousedown: this._handlers.setActive,
			},
			id: this._uid,
		}));

		const contentCell = this._layout.getCell("content");
		if (contentCell && contentCell.scrollView) {
			this.scrollView = contentCell.scrollView;
		}

		if (isHeader) {
			const header = (this.header = new Toolbar());
			if (this.config.title) {
				this.header.data.add({
					type: "title",
					value: this.config.title,
					id: "title",
					css: "title_max",
				});
				this._popup.setAttribute("aria-label", this.config.title);
			}
			if (this.config.closable) {
				this.header.data.add({
					type: "spacer",
				});
				this.header.data.add({
					id: "close",
					type: "button",
					view: "link",
					size: "medium",
					color: "secondary",
					circle: true,
					icon: "dxi dxi-close",
				});
				header.events.on(NavigationBarEvents.click, (id, e) => {
					if (id === "close") {
						this._hide(e);
					}
				});
			}
			layout.getCell("header").attach(header);
		}
		if (this.config.footer) {
			const footer = (this.footer = new Toolbar());
			layout.getCell("footer").attach(footer);
		}
		if (this.config.resizable) {
			layout.getCell("resizers").attach(() => this._drawResizers());
		}
	}
}
